// Email templates and the automatic signature for the staff send dialogs. Templates are shared: anyone can add or
// improve one, and each person picks their own default per kind. Placeholders like {client} are filled in from the
// document being sent, and turned back into placeholders when a filled-in message is saved as a template.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs } from '../../lib/api'
import { Button, Notice } from '../../ui'

export type TemplateKind = 'quote' | 'invoice' | 'statement' | 'chaser' | 'remittance' | 'receipt' | 'general'
export interface EmailTemplate { id: number; kind: TemplateKind; name: string; subject: string; body: string; created_by_name: string | null; updated_by_name: string | null; updated_at: string; is_default: boolean }
interface Signature { name: string; title: string; email: string; html: string; text: string }

export const PLACEHOLDER_HELP: Record<string, string> = {
  client: 'Client company', contact: 'Contact first name', number: 'Quote or invoice number', project: 'Project / site',
  total: 'Total', due_date: 'Due date', outstanding: 'Amount outstanding', my_name: 'Your name',
}

export function fillPlaceholders(text: string, vars: Record<string, string>) {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] != null && vars[k] !== '' ? vars[k] : m))
}
/** Turns this document's details back into placeholders, so a filled-in message can be saved as a reusable template. */
export function toPlaceholders(text: string, vars: Record<string, string>) {
  const pairs = Object.entries(vars).filter(([, v]) => v && v.trim().length >= 3).sort((a, b) => b[1].length - a[1].length)
  return pairs.reduce((t, [k, v]) => t.split(v).join(`{${k}}`), text)
}

export function useSignature() {
  return useQuery({ queryKey: ['me-signature'], queryFn: () => api.get<Signature>('/api/me/signature'), staleTime: 5 * 60_000 })
}

export function SignaturePreview({ compact }: { compact?: boolean }) {
  const sig = useSignature()
  if (sig.isLoading) return <p className="muted">Loading your signature…</p>
  if (sig.error || !sig.data) return <Notice tone="warn">Your signature could not be loaded.</Notice>
  return (
    <div className="stack" style={{ gap: 8 }}>
      <p className="muted" style={{ margin: 0 }}>
        Your signature is added automatically to quotes, invoices and statements you send from the hub: your name, your job title from HR
        {sig.data.title ? '' : ' (none set yet, ask an admin to add it in HR)'}, your email and the Briary logo.
        {!compact && ' You do not need to type a sign-off or signature in your message.'}
      </p>
      <iframe title="Your email signature" srcDoc={`<!DOCTYPE html><html><body style="margin:12px;background:#fff">${sig.data.html}</body></html>`}
        sandbox="allow-same-origin" style={{ width: '100%', height: compact ? 190 : 230, border: '1px solid var(--border, #e3e6e4)', borderRadius: 8, background: '#fff' }} />
    </div>
  )
}

/** Template picker for a send dialog: applies the person's default once, and lets them save, update, share and pick defaults. */
export function TemplateBar({ kind, vars, subject, message, setSubject, setMessage }: {
  kind: TemplateKind; vars: Record<string, string>; subject: string; message: string; setSubject: (s: string) => void; setMessage: (s: string) => void
}) {
  const qc = useQueryClient()
  const key = ['email-templates', kind]
  const list = useQuery({ queryKey: key, queryFn: () => api.get<{ templates: EmailTemplate[] }>(`/api/email-templates${qs({ kind })}`) })
  const templates = list.data?.templates ?? []
  const [selected, setSelected] = useState<number | ''>('')
  const [applied, setApplied] = useState(false)
  const [naming, setNaming] = useState<string | null>(null)
  const current = templates.find((t) => t.id === selected)
  const apply = (t: EmailTemplate | undefined) => {
    setSelected(t?.id ?? '')
    if (!t) return
    setSubject(fillPlaceholders(t.subject, vars))
    setMessage(fillPlaceholders(t.body, vars))
  }
  useEffect(() => {
    if (applied || !list.data) return
    setApplied(true)
    const d = templates.find((t) => t.is_default)
    if (d && !message.trim() && !subject.trim()) apply(d)
  }, [list.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => qc.invalidateQueries({ queryKey: key })
  const payload = (name: string) => ({ kind, name, subject: toPlaceholders(subject, vars), body: toPlaceholders(message, vars) })
  const create = useMutation({ mutationFn: (name: string) => api.post<{ id: number }>('/api/email-templates', payload(name)), onSuccess: async (r) => { setNaming(null); await refresh(); setSelected(r.id) } })
  const update = useMutation({ mutationFn: () => api.put(`/api/email-templates/${current!.id}`, payload(current!.name)), onSuccess: refresh })
  const remove = useMutation({ mutationFn: () => api.del(`/api/email-templates/${current!.id}`), onSuccess: async () => { setSelected(''); await refresh() } })
  const setDefault = useMutation({ mutationFn: (templateId: number | null) => api.put('/api/email-templates/default', { kind, templateId }), onSuccess: refresh })
  const err = [create, update, remove, setDefault].find((m) => m.error)?.error as Error | undefined
  return (
    <div className="stack" style={{ gap: 6 }}>
      <label className="field"><span className="field-label">Template</span>
        <select className="input" value={selected} onChange={(e) => apply(templates.find((t) => t.id === Number(e.target.value)))} disabled={list.isLoading}>
          <option value="">{templates.length ? 'No template (write your own)' : 'No templates yet, write a message and save it as one'}</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (your default)' : ''}{t.updated_by_name ? ` · ${t.updated_by_name}` : ''}</option>)}
        </select>
      </label>
      {err && <Notice tone="bad">{err.message}</Notice>}
      {naming != null ? (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <input className="input" style={{ flex: 1, minWidth: 180 }} autoFocus placeholder="Template name, e.g. Standard quote" value={naming} onChange={(e) => setNaming(e.target.value)} />
          <Button size="sm" variant="primary" busy={create.isPending} disabled={!naming.trim() || !message.trim()} onClick={() => create.mutate(naming.trim())}>Save template</Button>
          <Button size="sm" variant="ghost" onClick={() => setNaming(null)}>Cancel</Button>
        </div>
      ) : (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <Button size="sm" variant="ghost" disabled={!message.trim()} onClick={() => setNaming('')}>Save as new template</Button>
          {current && <Button size="sm" variant="ghost" busy={update.isPending} disabled={!message.trim()} onClick={() => update.mutate()}>Update “{current.name}”</Button>}
          {current && (current.is_default
            ? <Button size="sm" variant="ghost" busy={setDefault.isPending} onClick={() => setDefault.mutate(null)}>Stop using as my default</Button>
            : <Button size="sm" variant="ghost" busy={setDefault.isPending} onClick={() => setDefault.mutate(current.id)}>Make my default</Button>)}
          {current && <Button size="sm" variant="ghost" busy={remove.isPending} onClick={() => { if (window.confirm(`Delete the template “${current.name}” for everyone?`)) remove.mutate() }}>Delete</Button>}
        </div>
      )}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        Templates are shared with all staff. Placeholders: {Object.keys(PLACEHOLDER_HELP).filter((k) => k in vars).map((k) => `{${k}}`).join(' ')}. They fill in from this document, and this document’s details turn back into placeholders when you save.
      </p>
    </div>
  )
}
